/* eslint-disable @next/next/no-img-element */
'use client';

import { motion } from 'motion/react';
import React from 'react';

import { CMSLink } from '@/components/Link';
import RichText from '@/components/RichText';
import { Slider } from '@/components/Slider';
import type {
  ContentCardsBlock as ContentCardsBlockProps,
  Media,
} from '@/payload-types';
import { cn } from '@/utils/cn';

import { BlockSettings } from '../BlockSettings';
import styles from './block.module.scss';

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 50 },
};

export const ContentCardsBlock: React.FC<ContentCardsBlockProps> = (props) => {
  const {
    backgroundColor,
    individualSpacing,
    items,
    label,
    spacing,
    spacingBottom,
    spacingTop,
    title,
    tracking,
  } = props;

  if (!items || !Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <BlockSettings
      backgroundColor={backgroundColor}
      individualSpacing={individualSpacing}
      spacing={spacing}
      spacingBottom={spacingBottom}
      spacingTop={spacingTop}
    >
      <div className='container'>
        <Slider label={label || ''} title={title || ''}>
          {items.map((item, index) => {
            const {
              backgroundColor,
              content,
              heading,
              image,
              link,
              svgIllustration,
            } = item;

            const typedImage = image as Media;

            if (!content && !heading) {
              return null;
            }

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                style={{ willChange: 'opacity, transform' }}
                transition={{ duration: 0.3 }}
                className={cn(
                  styles.card,
                  backgroundColor ? `${styles[`card--${backgroundColor}`]}` : ''
                )}
              >
                {image && typedImage && (
                  <div
                    className={styles.image}
                    style={{
                      maskImage: `url(${(svgIllustration as Media)?.url})`,
                    }}
                  >
                    <img
                      loading='lazy'
                      src={typedImage?.sizes?.landscapeSmall?.url as string}
                      alt={typedImage.alt as string}
                      width={typedImage.sizes?.landscapeSmall?.width as number}
                      height={
                        typedImage.sizes?.landscapeSmall?.height as number
                      }
                    />
                  </div>
                )}
                <div className={styles.content}>
                  {heading && (
                    <h3 className={cn(styles.heading, 'h5')}>{heading}</h3>
                  )}
                  {content && (
                    <RichText
                      content={content}
                      className={styles.innerContent}
                      enableGutter={false}
                    />
                  )}
                </div>
                {link && (
                  <div className={styles.link}>
                    <CMSLink
                      {...link}
                      appearance='level-4'
                      className='button button--small'
                      tracking={{
                        type: 'Content cards',
                        headline: tracking?.cta || title || '',
                        cta: link.label || heading,
                      }}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </Slider>
      </div>
    </BlockSettings>
  );
};
